import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { formatFullName } from '@/lib/format';

export interface RecordingConsentDetail {
  id: string;
  userId: string;
  userEmail: string;
  userName?: string | null;
  printedName: string;
  consentType: 'video' | 'audio' | 'either';
  signedAt: string;
  revokedAt?: string | null;
  isCurrent: boolean;
  signatureData: string;
}

export interface RecordingConsentDetailModalProps {
  consentId: string | null;
  onClose: () => void;
}

function consentTypeLabel(type: RecordingConsentDetail['consentType']) {
  switch (type) {
    case 'video':
      return 'Video recording';
    case 'audio':
      return 'Audio-only recording';
    case 'either':
      return 'Video or audio recording';
    default:
      return type;
  }
}

function formatSignedAt(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function RecordingConsentDetailModal({
  consentId,
  onClose,
}: RecordingConsentDetailModalProps) {
  const [consent, setConsent] = useState<RecordingConsentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConsent = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    setConsent(null);
    const res = await api.get<RecordingConsentDetail>(`/admin/recording-consents/${id}`);
    if (res.error) {
      setError(res.error);
    } else if (res.data) {
      setConsent(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (consentId) {
      loadConsent(consentId);
    } else {
      setConsent(null);
      setError(null);
    }
  }, [consentId, loadConsent]);

  const leaderName =
    consent?.userName || formatFullName(null, null) || consent?.userEmail || 'Prayer Partner';

  return (
    <Modal
      isOpen={consentId !== null}
      onClose={onClose}
      title="Recording Consent Form"
      size="lg"
      panelClassName="max-w-2xl"
    >
      {loading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading consent form…</p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {!loading && !error && consent && (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                consent.isCurrent ? 'success' : consent.revokedAt ? 'warning' : 'default'
              }
            >
              {consent.isCurrent
                ? 'Current consent'
                : consent.revokedAt
                  ? 'Revoked'
                  : 'Superseded'}
            </Badge>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Signed {formatSignedAt(consent.signedAt)}
            </span>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 space-y-1 text-sm">
            <p>
              <span className="text-slate-500 dark:text-slate-400">User:</span>{' '}
              <strong>{consent.userName || consent.userEmail}</strong>
            </p>
            <p className="text-slate-600 dark:text-slate-400">{consent.userEmail}</p>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400">
            Thank you for your ministry to pray for others at Noon Prayer. We are grateful for your
            willingness to minister through personalized prayer recordings.
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300">
            I, <strong>{leaderName}</strong>, give permission to Times Square Church to record my voice,
            image, likeness, and words as I share prayer responses for individuals who submit prayer requests.
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            These recordings will be shared by private link or text message with the individual who
            requested prayer.
          </p>
          <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <p>Participation option selected:</p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={consent.consentType === 'video'}
                readOnly
                disabled
                className="rounded"
              />
              video recording
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={consent.consentType === 'audio'}
                readOnly
                disabled
                className="rounded"
              />
              audio-only recording
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={consent.consentType === 'either'}
                readOnly
                disabled
                className="rounded"
              />
              either video or audio recording
            </label>
          </div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {consentTypeLabel(consent.consentType)}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            I understand that while Times Square Church may disable the link for the recording after
            approximately 7 days, we cannot control whether the recipient copies, downloads, screen-records,
            forwards, posts, or otherwise shares it. I understand that Times Square Church is not
            responsible for how a recording is used or shared after it is delivered.
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            I may choose to stop participating in future recordings by notifying the Creative Department
            staff. I understand this will not affect recordings already shared.
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300">
            By signing below, I give consent for this use of my image and/or voice as described above.
          </p>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Signature
            </label>
            <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white overflow-hidden">
              <img
                src={consent.signatureData}
                alt={`Signature of ${consent.printedName}`}
                className="w-full h-40 object-contain bg-white"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Printed Name
              </label>
              <p className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm">
                {consent.printedName}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Date Signed
              </label>
              <p className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm">
                {formatSignedAt(consent.signedAt)}
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
